import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';

/**
 * CopyButton Component
 * 
 * A reusable copy button that copies text to clipboard and shows feedback.
 * Features:
 * - Copies text to clipboard using navigator.clipboard API
 * - Shows visual feedback (checkmark) when copied
 * - Auto-resets after 2 seconds
 * - Accessible with proper ARIA labels
 * - Styled for use in code blocks
 */
const CopyButton = ({ textToCopy, className = "" }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000); // Reset after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = textToCopy;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (fallbackErr) {
        console.error('Fallback copy failed: ', fallbackErr);
      }
    }
  };

  return (
    <button
      onClick={handleCopy}
      className={`absolute top-2 right-2 px-3 py-1.5 rounded flex items-center gap-1.5 bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 transition-colors duration-200 ${className}`}
      title={copied ? "Copied!" : "Copy to clipboard"}
      aria-label={copied ? "Copied to clipboard" : "Copy code to clipboard"}
    >
      {copied ? (
        <>
          <Check size={14} />
          <span className="text-sm font-medium">Copied</span>
        </>
      ) : (
        <>
          <Copy size={14} />
          <span className="text-sm font-medium">Copy</span>
        </>
      )}
    </button>
  );
};

export default CopyButton;
